// The model-element half of the construction tracking board.
//
// Same mount as the rest of the board (/projects/:id/field-tracking),
// and the same rules: the tree arrives one level at a time, and the
// exports are POSTs because the canvas the user is looking at travels
// in the body.
import { Modal } from "antd";
import { url, tokenRef } from "api";

const getHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const userToken = localStorage.getItem(tokenRef);
  if (userToken) headers.Authorization = `Bearer ${userToken}`;
  return headers;
};

const handle = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const base = (projectId) => `${url}/projects/${projectId}/field-tracking`;

const qs = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    q.set(k, Array.isArray(v) ? v.join(",") : String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

// One level of the tree. Pass category, then family, then typeName to
// walk down; passing typeName returns the elements themselves.
// Each conduit size's element ids (Model elements list, Conduits by size).
export const getConduitSizeUids = (projectId, params) =>
  fetch(`${base(projectId)}/elements/size-uids${qs(params)}`, { headers: getHeaders() }).then(handle);

export const getElementTree = (projectId, params) =>
  fetch(`${base(projectId)}/elements/tree${qs(params)}`, { headers: getHeaders() }).then(handle);

// Quantities read from the exporter's record files (lengths, areas,
// volumes per element, rolled up): build state, a rebuild, and the
// detailed tree - category > group > element.
export const getQuantityStatus = (projectId) =>
  fetch(`${base(projectId)}/quantities/status`, { headers: getHeaders() }).then(handle);

export const rebuildQuantities = (projectId) =>
  fetch(`${base(projectId)}/quantities/build`, { method: "POST", headers: getHeaders(), body: "{}" }).then(handle);

export const getQuantityTree = (projectId, params) =>
  fetch(`${base(projectId)}/quantities/tree${qs(params)}`, { headers: getHeaders() }).then(handle);

// The level rows in "Selection details" are selectable (owner,
// 2026-09-20). It takes THE SAME selection shapes everything else here
// takes, plus the levels picked, and answers in the same three shapes -
// so the page reads it with the code it already has.
export const getSelectionLevelUids = (projectId, body) =>
  fetch(`${base(projectId)}/selection/level-uids`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

// What the applied filter (header filters + gate rules) matched, as the
// id list the viewer lights - the same query a select-all write runs.
// Too many answers { tooMany: true, count }, never a partial list.
// A box drawn in the model, kept on the server so the quantities table can
// filter by it (thousands of ids do not fit a query string).
// -> { selectionId, count }
export const createQuantitySelection = (projectId, uniqueIds) =>
  fetch(`${base(projectId)}/quantities/selection`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify({ uniqueIds }),
  }).then(handle);

export const getFilterUids = (projectId, body) =>
  fetch(`${base(projectId)}/quantities/filter-uids`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

// Saved rule sets, for the signed-in person's COMPANY - offered on every
// project that company works on (owner, 2026-09-21). Saving a name the
// company already uses answers 409 with the existing set; send replace.
export const getRuleSets = (projectId) =>
  fetch(`${base(projectId)}/rule-sets`, { headers: getHeaders() }).then(handle);
export const saveRuleSet = (projectId, body) =>
  fetch(`${base(projectId)}/rule-sets`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);
export const deleteRuleSet = (projectId, setId) =>
  fetch(`${base(projectId)}/rule-sets/${setId}`, { method: "DELETE", headers: getHeaders() }).then(handle);

// The values a rule can choose from for one field (category, group, type,
// level), read from the model with their counts - never typed by hand.
export const getQuantityValues = (projectId, params) =>
  fetch(`${base(projectId)}/quantities/values${qs(params)}`, { headers: getHeaders() }).then(handle);

export const getElementStats = (projectId, params) =>
  fetch(`${base(projectId)}/elements/stats${qs(params)}`, { headers: getHeaders() }).then(handle);

// `tracked: 1` returns the status ROWS rather than the model's elements:
// everything somebody has touched, which is what the model view colours
// by.
export const getElementList = (projectId, params) =>
  fetch(`${base(projectId)}/elements/list${qs(params)}`, { headers: getHeaders() }).then(handle);

// Move elements to a status. Takes uniqueIds, or node keys, or a list -
// whichever the caller has, so a tree row does not have to expand into
// forty thousand ids to be marked installed.
//
// Moving elements back DOWN the ladder (installed -> procured, say) is a
// correction the server only makes once the person confirms it (409
// needsConfirm). Every caller gets that question here, so no page can
// skip it; a refusal rejects with `cancelled`.
const postStatus = (projectId, body) =>
  fetch(`${base(projectId)}/elements/status`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);
export const confirmDowngrade = (info, status) => new Promise((resolve) => {
  const n = info.wouldDowngrade || 0;
  Modal.confirm({
    title: `Move ${n.toLocaleString()} element${n === 1 ? "" : "s"} back?`,
    content: `${n.toLocaleString()} of the ${Number(info.count || 0).toLocaleString()} selected element${info.count === 1 ? " is" : "s are"} further along than "${String(status).replace("_", " ")}". Setting them moves them back down. You can undo this afterwards.`,
    okText: "Move them back", okButtonProps: { danger: true }, cancelText: "Cancel",
    onOk: () => resolve(true), onCancel: () => resolve(false),
  });
});
export const setElementStatus = async (projectId, body) => {
  try {
    return await postStatus(projectId, body);
  } catch (e) {
    if (e.status !== 409 || !e.data?.needsConfirm) throw e;
    if (!(await confirmDowngrade(e.data, body.status))) {
      const c = new Error("Cancelled"); c.cancelled = true; throw c;
    }
    return postStatus(projectId, { ...body, confirmDowngrade: true });
  }
};

// What a status change would do, without doing it: { count, byCurrent,
// wouldChange, wouldDowngrade }. Same body as setElementStatus.
export const previewElementStatus = (projectId, body) =>
  fetch(`${base(projectId)}/elements/status/preview`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

// Undo this person's last status change (one step, once).
export const undoElementStatus = (projectId) =>
  fetch(`${base(projectId)}/elements/status/undo`, {
    method: "POST", headers: getHeaders(), body: "{}",
  }).then(handle);

// Installed / being installed / procured / projected / remaining and % per
// tree node: by=category|family|type [, category, family, scope: 1, documentIds].
export const getStatusTotals = (projectId, params) =>
  fetch(`${base(projectId)}/elements/status-totals${qs(params)}`, { headers: getHeaders() }).then(handle);

// What the latest publish added: the elements the newest commit has that
// the previous one did not, paged and by category; and their ids, for
// the model view. `newSince: 1` on setElementStatus marks them all.
export const getNewElements = (projectId, params) =>
  fetch(`${base(projectId)}/elements/new${qs(params)}`, { headers: getHeaders() }).then(handle);
// `categories` (csv) asks for several at once - the diff list's chips are
// its parent rows, and ticking three of them is one question.
export const getNewElementUids = (projectId, params) =>
  fetch(`${base(projectId)}/elements/new/uids${qs(params)}`, { headers: getHeaders() }).then(handle);

export const getLists = (projectId) =>
  fetch(`${base(projectId)}/lists`, { headers: getHeaders() }).then(handle);

export const createList = (projectId, body) =>
  fetch(`${base(projectId)}/lists`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const updateList = (projectId, id, body) =>
  fetch(`${base(projectId)}/lists/${id}`, {
    method: "PATCH", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const deleteList = (projectId, id) =>
  fetch(`${base(projectId)}/lists/${id}`, { method: "DELETE", headers: getHeaders() }).then(handle);

// A page is a sheet, a slice of the model, and the alignment between
// them. A project has as many as it tracks on - one per level, usually,
// because each level has its own drawing.
export const getPages = (projectId) =>
  fetch(`${base(projectId)}/pages`, { headers: getHeaders() }).then(handle);

export const createPage = (projectId, body) =>
  fetch(`${base(projectId)}/pages`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const updatePage = (projectId, id, body) =>
  fetch(`${base(projectId)}/pages/${id}`, {
    method: "PATCH", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const deletePage = (projectId, id) =>
  fetch(`${base(projectId)}/pages/${id}`, { method: "DELETE", headers: getHeaders() }).then(handle);

// The tracked scope: which of the model's elements this project tracks,
// picked once from the tree and remembered per project. Untracked
// elements still draw; they carry no status colour and are not counted.
export const getScope = (projectId) =>
  fetch(`${base(projectId)}/scope`, { headers: getHeaders() }).then(handle);

// The ids an untick removed, for the tracking viewer's row filter.
//
// DELIBERATELY NOT CHAINED TO getScope: that one counts commit elements
// and has taken thirty seconds on a bad box, while this is two indexed
// reads and an empty answer when nothing is unticked. Chaining them
// would inherit the cost this exists to avoid.
//
// It answers 200 WITH a refusal body rather than an error status, so a
// non-200 here is a REAL failure - auth, network, a 404 - and must take
// the same unfiltered-and-say-so path rather than being read as "no
// exclusions".
export const getExcludedUids = (projectId) =>
  fetch(`${base(projectId)}/scope/excluded-uids`, { headers: getHeaders() }).then(handle);

export const saveScope = (projectId, body) =>
  fetch(`${base(projectId)}/scope`, {
    method: "PUT", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

// WHAT A SAVE WOULD REMOVE, before it removes it: { before, after,
// leaving, sections, sectionKeys, emptiedCategories } - and the custom
// categories it would empty are NAMED, because a person can act on
// "this empties Couplings (from Elbows)" and cannot act on "1 category".
// Writes nothing.
export const getScopePreview = (projectId, { nodeKeys = [], excludedNodeKeys = [], documentIds = "" } = {}) => {
  const q = new URLSearchParams();
  if (nodeKeys.length) q.set("nodeKeys", nodeKeys.join(","));
  if (excludedNodeKeys.length) q.set("excludedNodeKeys", excludedNodeKeys.join(","));
  if (documentIds) q.set("documentIds", documentIds);
  const s = q.toString();
  return fetch(`${base(projectId)}/scope/preview${s ? `?${s}` : ""}`, { headers: getHeaders() }).then(handle);
};

// Every uniqueId in the scope - what the model view paints. Streamed by
// the server, so a scope that is most of a 220k model still arrives as
// one array. `levels` narrows to a page's cut; `all: 1` ignores the
// scope and answers with the whole model (the cut of everything).
// EVERY PAGE'S ELEVATION HALF, FROM ONE LOAD OF THE ELEMENT BOXES.
//
// The live page resolves its own extras through /scope/uids. The
// multi-page export used to resolve NONE of them, so a printed section of
// a page with the elevation switches on showed its tagged levels while the
// model drew more.
//
// It is one call for all the pages because the cost is the LOAD, not the
// work: measured on BA03, one load is 2,230 ms and 194 MB held, and the
// per-page remainder is 253 ms of arithmetic. N calls would be N loads.
// -> { pages: [{ pageId, selectionId, added, range, note }] }
// A page whose extras cannot be resolved comes back with a SENTENCE, per
// page, and never silently as tagged-only.
// THE PART GROUPS OF A CATEGORY, with nothing selected (owner,
// 2026-10-02: "add a custom category for tracking, that counts assembly
// parts, from a dropdown list of all tracked categories, until we get to
// the assembly parts").
//
// The same endpoint the selection panel already uses - it takes category,
// family and type and does NOT require uniqueIds, because
// utils/derivedCategories.groupsFor matches the parent's place in the tree
// from the index. So a dropdown can ask about a whole category without a
// tick of a million elements becoming an $in.
export const getAssemblyGroups = (projectId, params) =>
  fetch(`${base(projectId)}/assembly/groups${qs(params)}`, { headers: getHeaders() }).then(handle);

// The custom part categories: the definitions, each with its numbers.
export const getDerivedCategories = (projectId, params) =>
  fetch(`${base(projectId)}/derived-categories${qs(params)}`, { headers: getHeaders() }).then(handle);

// THE PART IDS ONE DEFINITION MEANS, so a custom category can be selected,
// isolated and filtered in the model like any other category's elements
// (owner, 2026-10-03). Answers the same shapes as every other resolver here
// - { count, byPrefix, other } or { tooMany, count, limit } - so the caller
// uses helpers/treeRowUids.expandCompact and the existing refusal handling
// rather than a second code path.
// Remove one. A soft delete server-side (deleted: true), so a category that
// was counting toward progress stops counting without its history going.
export const deleteDerivedCategory = (projectId, id) =>
  fetch(`${base(projectId)}/derived-categories/${id}`, { method: 'DELETE', headers: getHeaders() }).then(handle);

export const getDerivedCategoryUids = (projectId, id, params) =>
  fetch(`${base(projectId)}/derived-categories/${id}/uids${qs(params)}`, { headers: getHeaders() }).then(handle);

// Create one, or - with dryRun - price it and write NOTHING. The dry run
// is the SAME code path as the create, deliberately: a preview computed
// differently from the real thing is a preview that can be wrong and
// nobody would ever find out.
export const createDerivedCategory = (projectId, body) =>
  fetch(`${base(projectId)}/derived-categories`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

export const getPagesCutUids = (projectId, body) =>
  fetch(`${base(projectId)}/pages/cut-uids`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

export const getScopeUids = (projectId, params) =>
  fetch(`${base(projectId)}/scope/uids${qs(params)}`, { headers: getHeaders() }).then(handle);

// The levels the model actually has, for the cut picker.
export const getLevels = (projectId, params) =>
  fetch(`${base(projectId)}/elements/levels${qs(params)}`, { headers: getHeaders() }).then(handle);

export const linkToActivity = (projectId, body) =>
  fetch(`${base(projectId)}/elements/schedule-link`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const syncToSchedule = (projectId, body) =>
  fetch(`${base(projectId)}/elements/schedule-sync`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

// One element, in full: what the model says it is (category, family,
// type, level) and what the field has said about it. Clicking in the
// model view used to answer with eight characters of a GUID.
export const getElement = (projectId, uniqueId, { documentIds } = {}) => {
  const q = new URLSearchParams();
  if (documentIds && documentIds.length) q.append("documentIds", documentIds.join(","));
  return fetch(
    `${base(projectId)}/elements/${encodeURIComponent(uniqueId)}${q.toString() ? `?${q}` : ""}`,
    { headers: getHeaders() }
  ).then(handle);
};

// The linked files in the project's model, for the file picker. Served
// by the projects route rather than the board's, because the model
// files gear on the project card reads the same list - one answer to
// "what is in this model" rather than two that can disagree.
export const getModelDocuments = (projectId) =>
  fetch(`${url}/projects/${projectId}/model-documents`, { headers: getHeaders() }).then(handle);

// Exports. The response is a file, so these resolve to a Blob and the
// caller saves it; an error still arrives as JSON and is thrown.
const download = async (projectId, kind, body, filename) => {
  const res = await fetch(`${base(projectId)}/elements/export.${kind}`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Export failed");
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  // The server names the file with the project prefix ("SPX-…"); ours is the fallback.
  a.download = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick rather than immediately: Safari has not
  // finished reading the blob when click() returns.
  setTimeout(() => URL.revokeObjectURL(href), 4000);
  return true;
};

export const exportXlsx = (projectId, body, filename = "construction-tracking.xlsx") =>
  download(projectId, "xlsx", body, filename);

export const exportPdf = (projectId, body, filename = "construction-tracking.pdf") =>
  download(projectId, "pdf", body, filename);

// EVERY ELEMENT IN SCOPE, no cap (owner, 2026-10-20): the server pages
// through the model and streams the rows, so the body is the scope alone.
export const exportCsv = (projectId, body, filename = "construction-tracking.csv") =>
  download(projectId, "csv", body, filename);

// PRINT: the same PDF, shown in a tab the page opened on the click (a tab
// opened after the export's long wait would be blocked as a popup), where
// the browser's own viewer prints it.
export const openPdf = async (projectId, body, win) => {
  const res = await fetch(`${base(projectId)}/elements/export.pdf`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Export failed");
  }
  const href = URL.createObjectURL(await res.blob());
  if (win && !win.closed) win.location.href = href;
  else window.open(href, "_blank");
  return true;
};

// Which elements a MODEL ELEMENTS tree row stands for, from the server
// (root-f5's POST /projects/:id/tree-uids, utils/treeUids): counted first
// on an index, refused over the limit with { tooMany, count, limit }, and
// answered compact (the 36-char document prefix once, then each element's
// id). The browser must never walk the tree for this: a collapsed row's
// children are placeholders, so walking it answers "nothing".
export const resolveNodeUids = (projectId, { category, family = "", typeName = "", prefixes = "", mode = "" }) =>
  fetch(`${url}/projects/${projectId}/tree-uids`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      category,
      // Today's contract (root-f5): group = family, name = type. Exact
      // `family` / `typeName` are being added; they are sent too, and
      // ignored until they land.
      group: family || undefined,
      name: typeName || undefined,
      family: family || undefined,
      typeName: typeName || undefined,
      prefixes: String(prefixes || "").split(",").map((s) => s.trim()).filter(Boolean),
      // 'auto' lets the server answer a big row as FRAGMENT HASHES instead
      // of a million ids (root-f5's rule isolate). Omitted, it answers as
      // it always did.
      ...(mode ? { mode } : {}),
    }),
  }).then(handle);

// Which elements a DETAILED TREE (quantities) row stands for. The same
// question as resolveNodeUids and the same answer shapes, over the other
// tree: its rows are the record's own category > group (elementquantities
// rc / rg), so they cannot be asked of the commit's category > family >
// type. One name per data source, on purpose (root-f5, 2026-09-20).
export const resolveRecordUids = (projectId, { category, group = null, material, size, documentIds = "", mode = "" }) =>
  fetch(`${base(projectId)}/quantities/uids`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      category,
      ...(group === null || group === undefined ? {} : { group }),
      // A size node of Conduits (back/utils/conduitSizes).
      ...(size === null || size === undefined ? {} : { material, size }),
      documentIds: String(documentIds || "").split(",").map((s) => s.trim()).filter(Boolean),
      ...(mode ? { mode } : {}),
    }),
  }).then(handle);

// The contractor's 6-week look-ahead, as read from the last Doc Control
// upload. Filing the workbook plans it; a PERSON applies it - the dates it
// writes (field forecast, drawing deadline, dependency) are what the field
// and BES work to, so nothing moves on an upload alone.
export const getLookaheadRuns = (projectId) =>
  fetch(`${base(projectId)}/lookahead/runs`, { headers: getHeaders() }).then(handle);

export const getLookaheadRun = (projectId, runId) =>
  fetch(`${base(projectId)}/lookahead/runs/${runId}`, { headers: getHeaders() }).then(handle);

export const applyLookaheadRun = (projectId, runId) =>
  fetch(`${base(projectId)}/lookahead/runs/${runId}/apply`, { method: "POST", headers: getHeaders(), body: "{}" }).then(handle);

export const dismissLookaheadRun = (projectId, runId) =>
  fetch(`${base(projectId)}/lookahead/runs/${runId}/dismiss`, { method: "POST", headers: getHeaders(), body: "{}" }).then(handle);

// Everything about what is selected, for the model view's "Selection
// details" tab. The selection is sent in the SAME shapes the bulk bar and
// the preview use ({ uniqueIds } | { nodeKeys } | { recordNodes }), so the
// page has one notion of "the selection" and the panel's count cannot
// drift from the bar's. `levels: 1` asks for the by-level table, which is
// the one costly part and so is fetched only when it is opened.
export const getSelectionSummary = (projectId, body) =>
  fetch(`${base(projectId)}/selection/summary`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  }).then(handle);

// Tag elements with a drawn coordination area (owner, 2026-10-09: trace the
// areas sheet, select what is under it, "tag their area field per
// element"). Mark's route; the server stamps who and when from the session,
// so no actor is ever sent. Body: { areaId (bare _id), uniqueIds } or
// { areaId, selectionId } for a box the server already holds.
export const tagElementArea = async (projectId, body) => handle(await fetch(
  `${url}/projects/${projectId}/element-areas/tag`,
  { method: "POST", headers: getHeaders(), body: JSON.stringify(body) },
));
